/**
 * F&O-style date shortcut parsing utility
 *
 * Supports the following input formats:
 * - `t` or `T` → today's date
 * - `+N` / `-N` → relative day offsets
 * - `D` or `DD` → day in current month
 * - `MMDD` → month + day (nearest date rule)
 * - `YYMMDD` → year + month + day (20YY)
 * - `YYYY-MM-DD` → ISO format
 * - `YYYYMMDD` → compact ISO format
 * - `DD/MM` or `D/M` → day/month (nearest date rule)
 * - `DD/MM/YYYY` → Swedish format
 *
 * For datetime fields, time can be appended with a space: `t 14:30`
 */

export interface ParsedDate {
  date: Date;
  hasTime: boolean;
}

export interface ParseResult {
  success: boolean;
  value?: ParsedDate;
  error?: string;
}

/**
 * Checks if a date components form a valid date
 */
function isValidDateComponents(year: number, month: number, day: number): boolean {
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
         date.getMonth() === month - 1 &&
         date.getDate() === day;
}

/**
 * Applies the "nearest date" rule for MMDD inputs:
 * - If the date is within 6 months in the past → current year
 * - Otherwise → next year
 */
function applyNearestDateRule(month: number, day: number, today: Date): number {
  const currentYear = today.getFullYear();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const candidateThisYear = new Date(currentYear, month - 1, day);

  // If candidate is today or in the future, use current year
  if (candidateThisYear >= todayStart) {
    return currentYear;
  }

  // Candidate is in the past - calculate days difference
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysDiff = Math.floor((todayStart.getTime() - candidateThisYear.getTime()) / msPerDay);

  // If within 6 months (less than 180 days) in the past, use current year
  if (daysDiff < 180) {
    return currentYear;
  }

  // Otherwise, use next year
  return currentYear + 1;
}

/**
 * Parses time string in HH:mm or HH:mm:ss format
 */
function parseTime(timeStr: string): { hours: number; minutes: number; seconds: number } | null {
  const match = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = match[3] ? parseInt(match[3], 10) : 0;

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
    return null;
  }

  return { hours, minutes, seconds };
}

/**
 * Main parsing function for date shortcuts
 */
export function parseDateShortcut(input: string, referenceDate?: Date): ParseResult {
  const today = referenceDate || new Date();
  const trimmed = input.trim();

  if (!trimmed) {
    return { success: false, error: 'Empty input' };
  }

  // Split date and time parts (space separator)
  const parts = trimmed.split(/\s+/);
  const datePart = parts[0];
  const timePart = parts.length > 1 ? parts.slice(1).join(' ') : null;

  let parsedTime: { hours: number; minutes: number; seconds: number } | null = null;
  if (timePart) {
    parsedTime = parseTime(timePart);
    if (!parsedTime) {
      return { success: false, error: 'Invalid time format' };
    }
  }

  let resultDate: Date | null = null;

  // 1. Today shortcut: t or T
  if (/^[tT]$/.test(datePart)) {
    resultDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }

  // 2. Relative day offsets: +N or -N
  else if (/^[+-]\d+$/.test(datePart)) {
    const offset = parseInt(datePart, 10);
    resultDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
  }

  // 3. ISO format: YYYY-MM-DD
  else if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [year, month, day] = datePart.split('-').map(Number);
    if (isValidDateComponents(year, month, day)) {
      resultDate = new Date(year, month - 1, day);
    }
  }

  // 4. Compact ISO format: YYYYMMDD
  else if (/^\d{8}$/.test(datePart)) {
    const year = parseInt(datePart.substring(0, 4), 10);
    const month = parseInt(datePart.substring(4, 6), 10);
    const day = parseInt(datePart.substring(6, 8), 10);
    if (isValidDateComponents(year, month, day)) {
      resultDate = new Date(year, month - 1, day);
    }
  }

  // 5. YYMMDD format (6 digits)
  else if (/^\d{6}$/.test(datePart)) {
    const year = 2000 + parseInt(datePart.substring(0, 2), 10);
    const month = parseInt(datePart.substring(2, 4), 10);
    const day = parseInt(datePart.substring(4, 6), 10);
    if (isValidDateComponents(year, month, day)) {
      resultDate = new Date(year, month - 1, day);
    }
  }

  // 6. MMDD format (4 digits)
  else if (/^\d{4}$/.test(datePart)) {
    const month = parseInt(datePart.substring(0, 2), 10);
    const day = parseInt(datePart.substring(2, 4), 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const year = applyNearestDateRule(month, day, today);
      if (isValidDateComponents(year, month, day)) {
        resultDate = new Date(year, month - 1, day);
      }
    }
  }

  // 7. Day only: D or DD (1-2 digits)
  else if (/^\d{1,2}$/.test(datePart)) {
    const day = parseInt(datePart, 10);
    if (day >= 1 && day <= 31) {
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      if (isValidDateComponents(year, month, day)) {
        resultDate = new Date(year, today.getMonth(), day);
      }
    }
  }

  // 8. DD/MM/YYYY format (Swedish)
  else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(datePart)) {
    const [dayStr, monthStr, yearStr] = datePart.split('/');
    const day = parseInt(dayStr, 10);
    const month = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);
    if (isValidDateComponents(year, month, day)) {
      resultDate = new Date(year, month - 1, day);
    }
  }

  // 9. DD/MM or D/M format
  else if (/^\d{1,2}\/\d{1,2}$/.test(datePart)) {
    const [dayStr, monthStr] = datePart.split('/');
    const day = parseInt(dayStr, 10);
    const month = parseInt(monthStr, 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const year = applyNearestDateRule(month, day, today);
      if (isValidDateComponents(year, month, day)) {
        resultDate = new Date(year, month - 1, day);
      }
    }
  }

  // No pattern matched
  if (!resultDate) {
    return { success: false, error: 'Invalid date format' };
  }

  // Apply time if provided
  if (parsedTime) {
    resultDate.setHours(parsedTime.hours, parsedTime.minutes, parsedTime.seconds, 0);
  } else {
    resultDate.setHours(0, 0, 0, 0);
  }

  return {
    success: true,
    value: {
      date: resultDate,
      hasTime: !!parsedTime,
    },
  };
}

/**
 * Formats a date to ISO date string (YYYY-MM-DD)
 */
export function formatDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a date to ISO datetime string without timezone (YYYY-MM-DDTHH:mm:ss)
 */
export function formatDateTime(date: Date): string {
  const dateStr = formatDateOnly(date);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${dateStr}T${hours}:${minutes}:${seconds}`;
}

/**
 * Parses a date string and returns the formatted value for the model
 * Returns null if parsing fails or input is empty
 */
export function parseAndFormatDate(input: string, isDateTime: boolean = false): string | null {
  if (!input.trim()) {
    return null;
  }

  const result = parseDateShortcut(input);
  if (!result.success || !result.value) {
    return null;
  }

  if (isDateTime) {
    return formatDateTime(result.value.date);
  }
  return formatDateOnly(result.value.date);
}
